import { useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "motion/react";
import { Upload, FileText, Eye, Download, Lock, RotateCcw, AlertCircle, CheckCircle2, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { convertBankPdfToXlsx, type ConversionResult } from "@/lib/pdf-to-xlsx";
import { formatCurrencyAmount } from "@/lib/currency";

type TryState = "idle" | "converting" | "done" | "error";

function generateCsv(rows: ConversionResult["rows"]): string {
  const header = ["Date", "Description", "Debit", "Credit", "Balance"];
  const escape = (v: string | number | undefined) => {
    if (v == null) return "";
    const s = String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const lines = rows.map((r) =>
    [r.date, r.merchant || r.description, r.debit, r.credit, r.balance]
      .map(escape)
      .join(",")
  );
  return [header.join(","), ...lines].join("\n");
}

export default function TryConverter() {
  const navigate = useNavigate();
  const [state, setState] = useState<TryState>("idle");
  const [result, setResult] = useState<ConversionResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [filename, setFilename] = useState<string>("");
  const [fileSize, setFileSize] = useState<string>("");
  const [isDragging, setIsDragging] = useState(false);

  const convertingRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const startConversion = useCallback((file: File) => {
    if (convertingRef.current) return;

    const isPdf =
      file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      setErrorMessage("Please select a PDF file.");
      setState("error");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setErrorMessage("File is too large. Maximum size is 10 MB.");
      setState("error");
      return;
    }

    convertingRef.current = true;
    setFilename(file.name);
    setFileSize(`${(file.size / 1024).toFixed(1)} KB`);
    setState("converting");

    convertBankPdfToXlsx(file, false)
      .then((res) => {
        setResult(res);
        setState("done");
      })
      .catch((err) => {
        setErrorMessage(
          err instanceof Error ? err.message : "Conversion failed. Try a different PDF."
        );
        setState("error");
      })
      .finally(() => {
        convertingRef.current = false;
      });
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) startConversion(file);
    },
    [startConversion]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) startConversion(file);
      e.target.value = "";
    },
    [startConversion]
  );

  const reset = useCallback(() => {
    convertingRef.current = false;
    setState("idle");
    setResult(null);
    setErrorMessage(null);
    setBannerDismissed(false);
    setFilename("");
    setFileSize("");
  }, []);

  const downloadCsv = useCallback(() => {
    if (!result) return;
    const csv = generateCsv(result.rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename.replace(/\.pdf$/i, "") + ".csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [result, filename]);

  const handleSignUp = useCallback(() => {
    navigate("/signup?returnUrl=/try");
  }, [navigate]);

  return (
    <motion.div
      className="w-full max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      {/* ---- LEFT PANEL: Bank Statement File ---- */}
      <div className="rounded-2xl border border-border bg-card overflow-hidden flex flex-col">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <Upload className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-semibold">Bank Statement File</span>
        </div>

        <div className="p-4 flex-1 flex flex-col">
          {/* IDLE */}
          {state === "idle" && (
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`flex-1 cursor-pointer rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-3 px-6 py-10 transition-colors ${
                isDragging
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50 hover:bg-muted/30"
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={handleFileInput}
              />
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-foreground">
                  Drop your PDF bank statement here
                </p>
                <p className="text-xs text-muted-foreground mt-1">or click to browse files</p>
              </div>
              <Button size="sm" className="rounded-full pointer-events-none">
                Select PDF File
              </Button>
              <p className="text-xs text-muted-foreground/60">
                🔒 PDF parsed locally for conversion.
              </p>
            </div>
          )}

          {/* CONVERTING */}
          {state === "converting" && (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 py-10 text-center">
              <Loader2 className="h-8 w-8 text-primary animate-spin" />
              <div>
                <p className="text-sm font-semibold">Converting {filename}…</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Running in your browser. Larger statements can take longer.
                </p>
              </div>
            </div>
          )}

          {/* DONE */}
          {state === "done" && result && (
            <div className="flex flex-col gap-3">
              <div className="rounded-xl border border-border p-3">
                <div className="flex items-start gap-3 mb-3">
                  <FileText className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold truncate">{filename}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{fileSize}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-2.5 border-t border-border">
                  <span className="text-xs text-muted-foreground">Transactions found:</span>
                  <span className="text-sm font-bold text-primary">
                    {result.summary.totalTransactions}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 dark:border-green-800/40 dark:bg-green-950/30 px-3 py-2 text-xs font-medium text-green-700 dark:text-green-400">
                <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
                Successfully extracted {result.summary.totalTransactions} transactions!
              </div>

              {!bannerDismissed && (
                <div className="flex items-center justify-between gap-3 rounded-xl border border-yellow-400/30 bg-yellow-50/80 dark:bg-yellow-950/20 px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-yellow-800 dark:text-yellow-300">
                      Sign up to download Excel
                    </p>
                    <p className="text-xs text-yellow-700/70 dark:text-yellow-400/70 mt-0.5">
                      No credit card · 5 free conversions
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <Button
                      size="sm"
                      className="rounded-full text-xs h-7 px-3"
                      onClick={handleSignUp}
                    >
                      Sign up free
                    </Button>
                    <button
                      onClick={() => setBannerDismissed(true)}
                      className="text-muted-foreground hover:text-foreground p-1"
                      aria-label="Dismiss"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              )}

              <Button variant="ghost" size="sm" onClick={reset} className="gap-2 self-start">
                <RotateCcw className="h-3.5 w-3.5" /> Try another PDF
              </Button>
            </div>
          )}

          {/* ERROR */}
          {state === "error" && (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 py-10 text-center">
              <AlertCircle className="h-8 w-8 text-destructive" />
              <div>
                <p className="text-sm font-semibold">Conversion failed</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-xs">{errorMessage}</p>
              </div>
              <Button onClick={reset} variant="outline" size="sm" className="gap-2 rounded-full">
                <RotateCcw className="h-3.5 w-3.5" /> Try another PDF
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* ---- RIGHT PANEL: Transaction Preview ---- */}
      <div className="rounded-2xl border border-border bg-card overflow-hidden flex flex-col">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <Eye className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-semibold">Transaction Preview</span>
        </div>

        {/* Empty state */}
        {(state === "idle" || state === "error") && (
          <div className="flex-1 flex flex-col items-center justify-center gap-2 py-12 text-muted-foreground">
            <FileText className="h-10 w-10 opacity-20" />
            <p className="text-sm font-medium">No data to preview</p>
            <p className="text-xs text-center max-w-[200px]">
              Open a bank statement PDF to see extracted transactions
            </p>
          </div>
        )}

        {/* Converting placeholder */}
        {state === "converting" && (
          <div className="flex-1 flex flex-col items-center justify-center gap-2 py-12 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin opacity-30" />
            <p className="text-xs">Extracting transactions…</p>
          </div>
        )}

        {/* Table + downloads */}
        {state === "done" && result && (
          <>
            <div className="flex-1 overflow-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted/50 border-b border-border sticky top-0">
                    <th className="text-left px-3 py-2.5 font-semibold text-foreground/60 whitespace-nowrap">
                      Date
                    </th>
                    <th className="text-left px-3 py-2.5 font-semibold text-foreground/60">
                      Description
                    </th>
                    <th className="text-right px-3 py-2.5 font-semibold text-foreground/60 whitespace-nowrap">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {result.rows.slice(0, 20).map((row, i) => (
                    <tr
                      key={i}
                      className={`border-b border-border/40 ${
                        i % 2 === 0 ? "bg-background" : "bg-muted/20"
                      }`}
                    >
                      <td className="px-3 py-2 font-mono text-muted-foreground whitespace-nowrap">
                        {row.date ?? "—"}
                      </td>
                      <td className="px-3 py-2 text-foreground/80 max-w-[200px] truncate">
                        {row.merchant || row.description || "—"}
                      </td>
                      <td className="px-3 py-2 text-right font-mono whitespace-nowrap">
                        {row.debit != null ? (
                          <span className="text-destructive/80">
                            -{formatCurrencyAmount(row.debit, result.currency)}
                          </span>
                        ) : row.credit != null ? (
                          <span className="text-green-600">
                            +{formatCurrencyAmount(row.credit, result.currency)}
                          </span>
                        ) : (
                          ""
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {result.rows.length > 20 && (
                <div className="px-3 py-2.5 text-center text-xs text-muted-foreground bg-muted/20 border-t border-border/40">
                  + {result.rows.length - 20} more rows — sign up to download all as Excel
                </div>
              )}
            </div>

            <div className="border-t border-border px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Download As
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 gap-2"
                  onClick={downloadCsv}
                >
                  <Download className="h-3.5 w-3.5" />
                  Download CSV
                </Button>
                <Button
                  size="sm"
                  className="flex-1 gap-2 opacity-50 cursor-not-allowed"
                  disabled
                  title="Create a free account to download Excel"
                >
                  <Lock className="h-3.5 w-3.5" />
                  Download Excel
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground/60 mt-1.5 text-right">
                🔒 Excel requires a free account
              </p>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}
